import React, { useEffect, useState, useCallback } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import styled from "styled-components";

import { LinkStyled } from "theme/CommonStyle";
import { PASSWORD_REGEX } from "constant";
import { Input, Button } from "components";
import {
  ResetPasswordActionCreators,
  VerifyResetPasswordTokenActionCreators,
} from "redux/rootActions";
import {
  selectIsLoading,
  selectErrorMessage,
} from "redux/resetpassword/resetpasswordStates";
import {
  selectIsVerifyResetPasswordToken,
  selectVerifyResetPasswordTokenErrorMessage,
  verifyResetPasswordTokenDataResponse,
} from "redux/verifyResetPasswordToken/verifyResetPasswordTokenStates";
import { ResetPasswordData } from "models/resetPassword";
import {
  Message,
  NoteText,
  WrapperLink,
} from "screens/forgotPasswordNotification/ForgotPasswordNotification";

const schema = yup.object().shape({
  password: yup
    .string()
    .trim()
    .required("password-required")
    .matches(PASSWORD_REGEX, "password-requirements"),
  confirmPassword: yup
    .string()
    .required("confirm-password-required")
    .oneOf([yup.ref("password")], "password-not-match"),
});

const ResetPassword: React.FC = () => {
  const { t }: any = useTranslation("translation", { keyPrefix: "auth" });
  const dispatch = useDispatch();
  const { push } = useHistory();

  const isLoading = useSelector(selectIsLoading);
  const isVerifyResetPasswordTokenLoading = useSelector(
    selectIsVerifyResetPasswordToken
  );

  const errorMessage = useSelector(selectErrorMessage);
  const verifyResetPasswordTokenErrorMessage = useSelector(
    selectVerifyResetPasswordTokenErrorMessage
  );

  const verifyResetPasswordTokenData = useSelector(
    verifyResetPasswordTokenDataResponse
  );
  const { watch, handleSubmit, control } = useForm({
    resolver: yupResolver(schema),
  });

  const [verifyStatus, setVerifyStatus] = useState<"valid" | "invalid" | "">(
    ""
  );

  const watchPasswordField = watch("password");
  const watchConfirmPasswordField = watch("confirmPassword");

  const params = new URLSearchParams(window.location.search);
  const reset_password_token = params.get("reset_password_token");

  useEffect(() => {
    setVerifyStatus("");
    dispatch(VerifyResetPasswordTokenActionCreators.handleResetAction());
  }, []);

  useEffect(() => {
    dispatch(ResetPasswordActionCreators.handleErrorAction(""));
  }, [watchPasswordField, watchConfirmPasswordField]);

  useEffect(() => {
    if (
      !isVerifyResetPasswordTokenLoading &&
      verifyResetPasswordTokenErrorMessage
    ) {
      setVerifyStatus("invalid");
      dispatch(VerifyResetPasswordTokenActionCreators.handleResetAction());
    }
  }, [verifyResetPasswordTokenErrorMessage, isVerifyResetPasswordTokenLoading]);

  useEffect(() => {
    if (!isVerifyResetPasswordTokenLoading && verifyResetPasswordTokenData) {
      setVerifyStatus("valid");
      dispatch(VerifyResetPasswordTokenActionCreators.handleResetAction());
    }
  }, [verifyResetPasswordTokenData, isVerifyResetPasswordTokenLoading]);

  useEffect(() => {
    if (reset_password_token) {
      dispatch(
        VerifyResetPasswordTokenActionCreators.verifyResetPasswordTokenAction({
          reset_password_token,
        })
      );
    }
  }, [reset_password_token]);

  useEffect(() => {
    if (errorMessage) {
      if (
        errorMessage.startsWith("リセットリンクの有効期限") ||
        errorMessage.startsWith("リセットリンクが無効です")
      ) {
        setVerifyStatus("invalid");
        dispatch(VerifyResetPasswordTokenActionCreators.handleResetAction());
        dispatch(ResetPasswordActionCreators.handleResetAction());
      }
    }
  }, [errorMessage]);

  const onSubmit = (data: { [x: string]: any }) => {
    const params: ResetPasswordData = {
      user: {
        password: data.password,
        password_confirmation: data.confirmPassword,
        reset_password_token: reset_password_token,
      },
    };
    dispatch(ResetPasswordActionCreators.resetPasswordUserAction(params));
  };

  const renderContent = useCallback(() => {
    switch (verifyStatus) {
      case "valid":
        return (
          <form onSubmit={handleSubmit(onSubmit)}>
            <Controller
              control={control}
              name="password"
              render={({ field: { onChange }, fieldState: { error } }) => (
                <Input
                  label={t("new-password")}
                  type="password"
                  onChange={onChange}
                  error={t(error?.message)}
                  marginForm="0 0 20px 0"
                  fs={16}
                  fwLabel={500}
                />
              )}
            />
            <Controller
              control={control}
              name="confirmPassword"
              render={({ field: { onChange }, fieldState: { error } }) => (
                <Input
                  label={t("confirm-password")}
                  type="password"
                  onChange={onChange}
                  error={t(error?.message)}
                  marginForm="0 0 20px 0"
                  fs={16}
                  fwLabel={500}
                />
              )}
            />
            <div style={{ position: "relative" }}>
              {errorMessage && (
                <div
                  style={{
                    color: "#FB2121",
                    fontSize: 12,
                    fontWeight: 500,
                    lineHeight: "14px",
                    position: "absolute",
                    top: -15,
                  }}
                >
                  {errorMessage === "は不正な値です"
                    ? t("enter-value-valid")
                    : errorMessage.startsWith("リセットリンクの有効期限") ||
                      errorMessage.startsWith("リセットリンクが無効です")
                    ? ""
                    : errorMessage}
                </div>
              )}
              <Button
                name={t("change-password")}
                type="submit"
                background="#2F8CAE"
                color="#fff"
                bdr="6px"
                width="100%"
                margin="4px 0 0 0"
                padding="8px 0px"
                border="none"
                fontWeight={700}
                fontSize={16}
                disabled={isLoading}
                style={{
                  marginTop:
                    errorMessage &&
                    errorMessage.startsWith("現在お使いのパスワードと同じです")
                      ? 16
                      : 2,
                }}
              />
            </div>
          </form>
        );
      case "invalid":
        return (
          <>
            <Message>
              パスワード再設定リンクの有効期限切れ、または無効なアクセスです
            </Message>
            <NoteText>
              ※セキュリティ保護のため有効期限が設けられております。お手数ですが、再度最初からお手続きをお願いいたします。
            </NoteText>
            <WrapperLink onClick={() => push("/forgot-password")}>
              <div>パスワード再設定のメールを再送する</div>
            </WrapperLink>
          </>
        );
      default:
        return <></>;
    }
  }, [verifyStatus, errorMessage]);

  return renderContent();
};

export default ResetPassword;
